"use client";

import { useConfig } from "@d2c-web/providers/config-provider";
import { cn } from "@scott/ui/client";
import {
  BikingIcon,
  CartIcon,
  ChevronDownIcon,
  Flag,
  GeoLocationIcon,
  HelpIcon,
  UserIcon,
} from "@ui-d2c/components/ui";
import type { CustomerNavigationBarComponent, LinkItem } from "@ui-d2c/types";
import Link from "next/link";
import { useParams, useSearchParams } from "next/navigation";
import type React from "react";
import { type PropsWithChildren, useEffect, useState } from "react";
import { registrationAccessToDataLayer } from "@/utils/gtm.utils";

interface CustomerNavigationBarProps {
  componentData?: CustomerNavigationBarComponent | null;
  // @ts-expect-error - UserDataSession type not imported
  userDataSession: UserDataSession | null;
  userDropdownContent: React.ReactNode;
}

const CustomerNavigationBar = ({
  componentData,
  userDataSession,
  userDropdownContent,
}: CustomerNavigationBarProps) => {
  const params = useParams();
  const { brand } = useConfig();
  const searchParams = useSearchParams();
  const [redirectAfterLogin, setRedirectAfterLogin] = useState<string | null>(
    null
  );
  const [accountLink, setAccountLink] = useState<LinkItem | undefined>(
    undefined
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: -
  useEffect(() => {
    const redirectParam = searchParams.get("redirect");
    if (redirectParam) {
      setRedirectAfterLogin(redirectParam);
    } else {
      const rawUrl =
        window.location.pathname +
        window.location.search +
        window.location.hash;
      const urlToRedirect = btoa(encodeURIComponent(rawUrl));
      setRedirectAfterLogin(urlToRedirect);
    }
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: -
  useEffect(() => {
    if (componentData?.accountLink?.text && redirectAfterLogin) {
      setAccountLink({
        ...componentData.accountLink,
        text: userDataSession
          ? `${userDataSession.firstName.toUpperCase()} ${userDataSession.lastName.toUpperCase()}`
          : componentData.accountLink.text,
        link: `${componentData?.accountLink.link}?redirect=${redirectAfterLogin}`,
      });
    }
  }, [params, componentData, userDataSession, redirectAfterLogin]);

  const language = Array.isArray(params.lang) ? params.lang[0] : params.lang;
  const divClass = "h-full w-px bg-neutral-light mx-0 shrink-0";

  return (
    <div
      className={cn(
        "flex flex-row bg-nav-bg-primary",
        brand.isScott && "border-neutral-light border-b"
      )}
    >
      {componentData && (
        <div className="container flex w-full min-w-0 flex-row justify-between">
          <div className="flex shrink-0 flex-row items-center">
            <div
              className={
                "mx-0 flex h-full w-px items-center justify-center bg-neutral-light"
              }
            />
            <Link
              className={"text-followus-normal hover:text-followus-hover"}
              href="/country"
            >
              <div className="px-2">
                <Flag
                  countryCode={componentData.countryLink?.text || "GLOBAL"}
                  label={language?.toUpperCase()}
                />
              </div>
            </Link>
            <div className={divClass} />
          </div>
          <div className="flex min-w-0 flex-row items-center">
            <div className={divClass} />
            {componentData.helpLink?.link && (
              <>
                <AnchorElement
                  icon={<HelpIcon size="sm" />}
                  link={componentData.helpLink}
                />
                <div className={divClass} />
              </>
            )}
            {componentData.dealerLink?.link && (
              <>
                <AnchorElement
                  icon={<GeoLocationIcon size="sm" />}
                  link={componentData.dealerLink}
                />
                <div className={divClass} />
              </>
            )}
            {componentData.bikeRegistrationLink?.link && (
              <>
                <AnchorElement
                  icon={<BikingIcon />}
                  link={componentData.bikeRegistrationLink}
                  onClick={() =>
                    registrationAccessToDataLayer(
                      params.country ?? "",
                      params.lang ?? "",
                      "Header"
                    )
                  }
                />
                <div className={divClass} />
              </>
            )}
            {componentData.minicartLink?.link && (
              <>
                <AnchorElement
                  icon={<CartIcon size="sm" />}
                  link={componentData.minicartLink}
                />
                <div className={divClass} />
              </>
            )}
            {!userDataSession && accountLink?.link && (
              <>
                <AnchorElement
                  icon={<UserIcon size="sm" />}
                  link={accountLink}
                />
                <div className={divClass} />
              </>
            )}
            {userDataSession && accountLink?.link && (
              <>
                <DropDownElement
                  endIcon={<ChevronDownIcon size="sm" />}
                  label={`${userDataSession.firstName.toUpperCase()} ${userDataSession.lastName.toUpperCase()}`}
                  startIcon={<UserIcon size="sm" />}
                >
                  {userDropdownContent}
                </DropDownElement>
                <div className={divClass} />
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

CustomerNavigationBar.displayName = "CustomerNavigationBar";

export { CustomerNavigationBar };

interface anchorProps {
  link: LinkItem;
  icon: React.ReactNode;
  className?: string;
  onClick?: () => void;
}
const AnchorElement: React.FC<anchorProps> = ({
  link,
  icon,
  className,
  onClick,
}) => (
  <a
    aria-label={link.text || undefined}
    className={`${className} flex h-8 min-w-0 max-w-full items-center justify-center gap-2 whitespace-nowrap border-none px-4 py-2 font-nav-heading font-normal text-1xs text-nav-customer-text-secondary uppercase tracking-widest hover:text-nav-customer-text-secondary-hover focus:text-neutral-darker`}
    href={link.link}
    onClick={onClick}
  >
    <span aria-hidden="true" className="shrink-0">
      {icon}
    </span>
    <span className="hidden min-w-0 max-w-full truncate md:block">
      {link.text}
    </span>
  </a>
);

interface DropDownElementProps {
  label: string;
  startIcon: React.ReactNode;
  endIcon: React.ReactNode;
}

const DropDownElement: React.FC<PropsWithChildren<DropDownElementProps>> = ({
  label,
  startIcon,
  endIcon,
  children,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <div
      className="relative"
      onBlur={() => setMenuOpen(false)}
      onClick={() => setMenuOpen(true)}
      onMouseEnter={() => setMenuOpen(true)}
      onMouseLeave={() => setMenuOpen(false)}
    >
      <div className="flex h-8 items-center justify-center gap-2 whitespace-nowrap border-none px-4 py-2 font-normal text-neutral-dark text-xs uppercase tracking-widest hover:text-neutral-darker focus:text-neutral-darker">
        <span aria-hidden="true">{startIcon}</span>
        <span className="hidden min-w-0 max-w-full truncate md:block">
          {label}
        </span>
        <span aria-hidden="true">{endIcon}</span>
      </div>
      {menuOpen && (
        <div className="absolute right-[-0.1em] z-[9999] min-w-[180px] border border-neutral-200 bg-neutral-100 p-3 shadow-lg">
          {children}
        </div>
      )}
    </div>
  );
};
